import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
	getS3BatchJobs,
	createAndRunBatchJobFromFailures,
} from "../../actions/adminActions";

import CustomizableTable from "../Tables/CustomizableTable";
import InteractiveTableCell from "../Tables/InteractiveTableCell";
import { DropDownButtonWithRouter } from "../DropDownButton";
import { LexWorkplaceGavelIcon } from "../LexWorkplaceIcons";
import { IconSearch } from "@tabler/icons-react";

function AdminBatchJobs({ tenantID, tenantName, refreshTick }) {
	const dispatch = useDispatch();
	const [batchJobs, setBatchJobs] = useState([]);
	const [loading, setLoading] = useState(true);

	const title = tenantName ? `"${tenantName}", tenantID:${tenantID}` : "";

	const loadBatchJobs = useCallback(() => {
		setLoading(true);
		dispatch(getS3BatchJobs(tenantID))
			.then((res) => {
				setBatchJobs(
					res.jobs.Jobs.map((job) => ({
						...job,
						ProgressSummarySuccess: `${job.ProgressSummary.NumberOfTasksSucceeded} / ${job.ProgressSummary.TotalNumberOfTasks}`,
						ProgressSummaryFailure: `${job.ProgressSummary.NumberOfTasksFailed} / ${job.ProgressSummary.TotalNumberOfTasks}`,
					}))
				);
				setLoading(false);
			})
			.catch((err) => {
				console.error(err);
				setLoading(false);
			});
	}, [tenantID, dispatch]);

	useEffect(() => {
		loadBatchJobs();
	}, [loadBatchJobs, refreshTick]);

	const batchJobsColumnConfig = useMemo(
		() => [
			{ Header: "CreationTime", accessor: "CreationTime" },
			{ Header: "JobId", accessor: "JobId" },
			{ Header: "Description", accessor: "Description", width: 80 },
			{ Header: "Status", accessor: "Status" },
			{
				Header: "Successes",
				accessor: "ProgressSummarySuccess",
			},
			{
				Header: "Failures",
				accessor: "ProgressSummaryFailure",
				Cell: (row) => (
					<InteractiveTableCell
						menuItems={[
							{
								text: "re-try failures as new batch job",
								disabled: row.original.Status !== "Complete",
								onSelect: () =>
									createAndRunBatchJobFromFailures(
										tenantID,
										row.original.JobId
									),
							},
						]}
					>
						<span>{row.original.ProgressSummaryFailure}</span>
					</InteractiveTableCell>
				),
			},
		],
		[tenantID]
	);

	return (
		<div className="fullHeightFlex">
			<CustomizableTable
				actionButtons={({ openCsvExport }) => (
					<div
						style={{
							marginLeft: "auto",
							marginTop: 0,
						}}
						className="buttonGroup"
					>
						<DropDownButtonWithRouter
							title="Actions"
							iconComponent={<LexWorkplaceGavelIcon />}
							pullRight
							menuItems={[
								{
									text: "Export Data as CSV",
									onSelect: openCsvExport,
								},
							]}
						/>
					</div>
				)}
				leftSideFilterSpace={
					<h4 style={{ paddingLeft: 12, alignContent: "center" }}>
						<IconSearch
							size={20}
							style={{
								marginRight: 10,
								verticalAlign: "bottom",
							}}
						/>
						{title}
					</h4>
				}
				className="fullWidth"
				loading={loading}
				data={batchJobs}
				columns={batchJobsColumnConfig}
				csvExport={{
					fileName: "admin-batch-jobs",
				}}
				noResultsText={"No Batch jobs to show"}
				autoResize
				tableUniqueID="/admin/batchJobsForFirm"
			/>
		</div>
	);
}

export default AdminBatchJobs;
